import React, { useState, useCallback } from "react"

import { ReactComponent as FormSvg } from 'resources/icons/form/form_solid.svg'
import { ReactComponent as MeetingSvg } from 'resources/icons/general/meeting_solid.svg'
import { ReactComponent as UploadSvg } from 'resources/icons/general/upload_solid.svg'
import { ReactComponent as ApprovalSvg } from 'resources/icons/general/approval_solid.svg'
import { ReactComponent as ActionSvg } from 'resources/icons/general/action_solid.svg'

import { ReactComponent as ConnectSvg } from 'resources/icons/general/connect_solid.svg'
import { ReactComponent as EditSvg } from 'resources/icons/field/edit_solid.svg'
import { ReactComponent as MailSvg } from 'resources/icons/general/mail_send_solid.svg'
import { ReactComponent as ApprovalUpdateSvg } from 'resources/icons/general/approval_update_solid.svg'
import { ReactComponent as ApprovalWaitSvg } from 'resources/icons/general/approval_wait_solid.svg'

import Item from './c/Item/Item'

import * as st from './QuickActions.styled.js'

const actions = [
  {
    id: "form",
    name: "Submit form",
    Icon: FormSvg,
    description: "Build custom forms and save them as templates to reuse when needed, ensuring accurate data collection. Save submitted data in the CRM if necessary.",
    image: {
      src: "//cdn-platform.scope-dev.ws/website/images/screens/index/actions/form.webp",
      src2x: "//cdn-platform.scope-dev.ws/website/images/screens/index/actions/form@2x.webp",
      width: 520,
      height: 600,
    },
    feature1Name: "Send results to CRM",
    Feature1Icon: ConnectSvg,
    feature2Name: "Fully customizable",
    Feature2Icon: EditSvg,
  },
  {
    id: "meeting",
    name: "Schedule meeting",
    Icon: MeetingSvg,
    description: "A simple task with an embedded Calendar provides the easiest way to ask your customer to schedule a call.",
    image: {
      src: "//cdn-platform.scope-dev.ws/website/images/screens/index/actions/meeting.webp",
      src2x: "//cdn-platform.scope-dev.ws/website/images/screens/index/actions/meeting@2x.webp",
      width: 520,
      height: 600,
    },
    feature1Name: "Easy to use",
    Feature1Icon: MailSvg,
  },
  {
    id: "file-request",
    name: "File requests",
    Icon: UploadSvg,
    description: "Ask your customer to upload files you need from them.",
    image: {
      src: "//cdn-platform.scope-dev.ws/website/images/screens/index/actions/upload.webp",
      src2x: "//cdn-platform.scope-dev.ws/website/images/screens/index/actions/upload@2x.webp",
      width: 520,
      height: 600,
    },
    feature1Name: "Customizable",
    Feature1Icon: EditSvg,
  },
  {
    id: "approvals",
    name: "Approvals",
    Icon: ApprovalSvg,
    description: "The easiest and efficient way for back-and-forth communication when you need to approve something.",
    image: {
      src: "//cdn-platform.scope-dev.ws/website/images/screens/index/actions/approve.webp",
      src2x: "//cdn-platform.scope-dev.ws/website/images/screens/index/actions/approve@2x.webp",
      width: 520,
      height: 600,
    },
    feature1Name: "Change requests",
    Feature1Icon: ApprovalUpdateSvg,
    feature2Name: "Reminders",
    Feature2Icon: ApprovalWaitSvg,
  },
  {
    id: "actions",
    name: "Action Buttons",
    Icon: ActionSvg,
    description: "Create your own custom button with a link that your customer needs to follow or a file they need to download.",
    image: {
      src: "//cdn-platform.scope-dev.ws/website/images/screens/index/actions/button.webp",
      src2x: "//cdn-platform.scope-dev.ws/website/images/screens/index/actions/button@2x.webp",
      width: 520,
      height: 600,
    },
    feature1Name: "Easy to use",
    Feature1Icon: MailSvg,
  },
]

const QuickActions = () => {
  const [activeId, setActiveId] = useState("form");

  const currentAction = actions.find(({ id }) => id === activeId) || {};

  const onOpenMenu = useCallback((id, event) => {
    setActiveId(id);
    setTimeout(() => {
      const element = event.target;
      const rect = element.getBoundingClientRect();
      const offsetTop = rect.top + window.pageYOffset - 80;

      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
    }, 350);
  }, [])

  return (
    <st.Wrap>
      <st.Content>
        <st.LeftSection>
          <st.MessageWrap>
            <st.ArrowBlock>
              <img
                src="//cdn-platform.scope-dev.ws/website/images/illustrations/index-actions/cursor.png"
                srcSet="//cdn-platform.scope-dev.ws/website/images/illustrations/index-actions/cursor.png 1x, //cdn-platform.scope-dev.ws/website/images/illustrations/index-actions/cursor@2x.png 2x"
                width={72}
                height={72}
                alt="Cursor"
              />
            </st.ArrowBlock>
            <h2>Quick actions</h2>
            <p>The simplest way to ask your customer to do somethings</p>
          </st.MessageWrap>

          <st.ListWrap>
            <st.List>
              {actions.map(({
                id, name, description, image, Icon,
                feature1Name, Feature1Icon,
                feature2Name, Feature2Icon,
              }) => (
                <Item
                  key={id}
                  name={name}
                  image={image}
                  Icon={Icon}
                  isActive={activeId === id}
                  onHover={() => setActiveId(id)}
                  onClick={(event) => onOpenMenu(id, event)}
                >
                  <st.InnerItemInfo
                    description={description}
                    feature1Name={feature1Name}
                    feature2Name={feature2Name}
                    Feature1Icon={Feature1Icon}
                    Feature2Icon={Feature2Icon}
                  />
                </Item>
              ))}
            </st.List>
            <st.MainItemInfo
              key={activeId}
              description={currentAction.description}
              feature1Name={currentAction.feature1Name}
              feature2Name={currentAction.feature2Name}
              Feature1Icon={currentAction.Feature1Icon}
              Feature2Icon={currentAction.Feature2Icon}
            />
          </st.ListWrap>
        </st.LeftSection>

        <st.MainScreen
          id={activeId}
          image={currentAction.image}
          Icon={activeId === "form" ? FormSvg
                : activeId === "meeting" ? MeetingSvg
                : activeId === "file-request" ? UploadSvg
                : activeId === "approvals" ? ApprovalSvg
                : activeId === "actions" && ActionSvg}
        />
      </st.Content>
    </st.Wrap>
  )
}

export default QuickActions
